import Vue from 'vue';
import Moment from 'moment';
import EdInputModel from '@/models/ed-input.model';
import EdFormModel from '@/models/ed-form.model';
import KEYS from '@/values/keys';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { required, maxLength, numeric } from 'vuelidate/lib/validators';
import { CourseService } from '@/courses/services/courses.service';
import EdImageCreated from '@/content/pages/create-lesson/modals/ed-lesson-create-image/components/ed-image-created/ed-image-created.vue';
import { ALL_CONTENT_ROUTES } from '@/app/private/my-content/views/all/all.router';
import { COURSE_TEMPLATE_EVENTS, PRODUCT_TYPES, AUTOMATIC_FOLDER_BUILDER } from '@/course-templates/values/course-templates.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { IParamsCreateComponent } from '@/courses/interfaces/params-create-component.interface';
import { cloneDeep, isEqual } from 'lodash';

@Component({
  name: 'ed-create-product',
  components: {
    EdImageCreated
  }
})
export default class EdCreateProductComponent extends Vue {
  @Prop() readonly initFormData: any;
  @Prop() readonly elementId: any;
  @Prop() readonly saving: boolean;
  @Prop() readonly title: string;
  @Prop({ default: false }) readonly isQuickSubject: boolean;
  @Prop({ default: PRODUCT_TYPES.SUBJECT }) readonly productType: PRODUCT_TYPES;

  public form: any = null;
  public loading: boolean = false;
  public formData: any = {};
  public courseForm: any = {};
  public dropzoneMode: string = KEYS.DROPZONE_CONFIG.SINGLE;
  public uploadedImage: any = null;
  public subjects: Array<any> = [];
  public subjectSelected: string = '';
  public switchToggle = false;
  public processImage: boolean = false;
  public removeImage: boolean = false;
  public productTypes: any = PRODUCT_TYPES;
  public schoolCalendarList: any = [];
  public selectedSchoolCalendar: any = null;
  public initSchoolCalendar: any = null;
  public automaticFolder: string = AUTOMATIC_FOLDER_BUILDER.NO_AUTOMATIC;
  public automaticFolderTypes = AUTOMATIC_FOLDER_BUILDER;
  public components: IParamsCreateComponent[] = [];
  public componentsSelected: IParamsCreateComponent[] = [];
  public componentsSelectedOriginal: IParamsCreateComponent[] = [];
  public componentsChanged: boolean = false;
  public componentsChangedWarning: boolean = false;
  public componentsSelectedIds: number[] = [];
  public componentsSelectedOriginalIds: number[] = [];

  /* Computed */
  get dropOptions(): any {
    return {
      url: 'http://eduac.com',
      autoProcessQueue: false,
      acceptedFiles: 'image/png,.jpg,.jpeg',
      uploadMultiple: false,
      addRemoveLinks: true,
      parallelUploads: 1,
      maxFiles: 1,
      createImageThumbnails: false,
      timeout: 600000,
      maxFilesize: 2
    }
  }

  get organization(): any {
    return this.$store.state.ToolsStore.institutionInfo;
  }

  get switchToggleTitle() {
    return this.switchToggle ? `${this.$i18n.t('courses.createCourse.onToggleGrading')}` : `${this.$i18n.t('courses.createCourse.offToggleGrading')}`;
  }

  /* Images Computed */
  get imagePreview(): any {
    if(this.uploadedImage) {
      return {
        hash: this.uploadedImage.hash || 0,
        thumbnailMediumUrl: URL.createObjectURL(this.uploadedImage.file),
        file: this.uploadedImage.file
      }
    } else return null
  }

  get gradeLevels() {
    return this.$store.state.CourseTemplateStore.templatesGradeLevelsOptions
      .filter(el => el.value);
  }

  get formDisabled() {
    let disabled = !this.form?.valid || this.saving || !this.subjectSelected;

    if (!!this.form?.suggestedLessonDuration?.value && Number(this.form?.suggestedLessonDuration?.value) <= 0) {
      disabled = true;
    }

    if(this.productType === this.productTypes.BUILDER) {
      disabled = disabled || !this.selectedSchoolCalendar;
    }
    
    return disabled;
  }

  get activeSchoolCalendar () {
    return this.$store.state.SchoolCalendarStore.schooldaysList.schoolDayId;
  }

  /* Methods */
  init(name: string = '', 
      description: string = '', 
      defaultDueDate:string = '3', 
      defaultLastDate: string = '0',
      suggestedGradeId = '',
      actualGradeId = ''
   ) {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(200)}, true),
      EdInputModel('description', description),
      EdInputModel('defaultDueDate', defaultDueDate, { numeric }, true),
      EdInputModel('defaultLastDate', defaultLastDate, { numeric }, true),
      EdInputModel('suggestedGradeId', suggestedGradeId),
      EdInputModel('actualGradeId', actualGradeId),
      EdInputModel('subjectDays', '', { numeric }, true ),
      EdInputModel('blockDuration', '', { numeric }, true ),
      EdInputModel('suggestedLessonDuration', '', { numeric }, true )
    ]);
    if(this.productType === this.productTypes.TEMPLATE) {
      this.form.setValidator('suggestedGradeId', 'required', required); 
      !this.form.suggestedGradeId.value && this.form.setValid('suggestedGradeId', false);
    }
    if(this.productType === this.productTypes.BUILD) {
      this.form.setValidator('subjectDays', 'required', required); 
      !this.form.subjectDays.value && this.form.setValid('subjectDays', false);
      this.form.setValidator('blockDuration', 'required', required); 
      !this.form.blockDuration.value && this.form.setValid('blockDuration', false);
    }
  }

  /* Image Methods */
  public async onImageUploadedSuccess({file, res}) {
    this.initFormData.id = res.id;
  }

  public onFileAdd(file: File) {
    let data = {
      file: file,
      hash: null
    }
    this.processImage = true;
    this.removeImage = false;
    this.uploadedImage = data;
  }

  public onError() {
    this.uploadedImage = null;
  }

  public removeImg(data: any) {
    this.uploadedImage && (this.$refs.dropzoneRef as any).removeFile(data.file);
    this.uploadedImage = null;
    this.removeImage = true;
  }

  public showErroModal() {
    this.$EdModal.createMessageModal({
      title: this.title,
      body: this.$i18n.t('courses.errors.errorSavingCover'),
      type: KEYS.EVENT_TYPES.DANGER
    });
  }
  /* End image Methods */

  goBack() {
    if(this.productType == this.productTypes.SUBJECT) 
      this.$router.push({ name: ALL_CONTENT_ROUTES.SUBJECTS.NAME });
    else 
      this.$router.push({ name: ALL_CONTENT_ROUTES.TEMPLATES.NAME });
  }

  async save() {
    this.formData = {
      'categoryId': this.subjectSelected,
      'contentSummary': this.form.description.value,
      'contentTitle': this.form.name.value,
      'defaultDueDateForTasks': this.form.defaultDueDate.value,
      'defaultLastDateForTasks': this.form.defaultLastDate.value,
      'organizationId': this.organization.length > 0 ? this.organization[0].organizationId : '',
      'hasGrading': this.switchToggle,
      'deleteCover': this.removeImage,
      'suggestedLessonDuration': this.form.suggestedLessonDuration.value,
    }
    
    if (this.productType === this.productTypes.TEMPLATE) {
      this.formData.actualGradeId = this.form.actualGradeId.value;
      this.formData.suggestedGradeId = this.form.suggestedGradeId.value;
    }
    if (this.productType === this.productTypes.BUILDER || this.productType === this.productTypes.BUILD) {
      this.formData.schoolDay = this.selectedSchoolCalendar;
      if(!this.processImage && this.initFormData.imgHash) {
        this.formData.coverImg = this.initFormData.imgHash;
        this.formData.coverImageUrl = this.initFormData.img;
      }

      this.formData.componentsSelected = this.componentsSelected;
      
      if(this.componentsSelectedOriginal.length <= 0) {
        this.componentsSelectedOriginal = cloneDeep(this.componentsSelected);
      } 
      
      this.componentsChanged = (!isEqual(this.componentsSelected, this.componentsSelectedOriginal));

      this.componentsSelectedOriginal = cloneDeep(this.componentsSelected);
      this.componentsChangedWarning = false;
      
      this.formData.existChanges = this.initSchoolCalendar !== this.selectedSchoolCalendar || this.componentsChanged; 
      this.formData.automaticFolderType = this.automaticFolder;
      
      this.formData.componentsChanged = this.componentsChanged;
    }

    if(this.productType === this.productTypes.BUILD) {
      this.formData.subjectDays = this.form.subjectDays.value;
      this.formData.blockDuration = this.form.blockDuration.value;
    }

    if(this.removeImage) {
      this.uploadedImage = null;
      this.formData.coverImg = null;
      this.formData.coverImageUrl = null;
    } 
    let uploadedImage = this.uploadedImage;
    if(!this.processImage) uploadedImage = null;
    this.formData.componentsSelected = this.components.filter(c => this.componentsSelectedIds.includes(c.id));

    if(this.componentsChanged || this.processImage || this.removeImage || this.initSchoolCalendar !== this.selectedSchoolCalendar || this.productType !== this.productTypes.BUILDER){
      this.$edEventBus.$emit(COURSE_TEMPLATE_EVENTS.SAVE_COURSE_FORM, {formData: this.formData, uploadedImage});
    } 

    this.processImage = false;
    this.removeImage = false;
    this.initSchoolCalendar = this.selectedSchoolCalendar;
    
  }

  changeComponent() {
    this.componentsChangedWarning = this.componentsSelectedOriginalIds.length > 0
      && !isEqual(this.componentsSelectedIds, this.componentsSelectedOriginalIds);
  }

  @Watch('componentsSelectedIds')
  onComponentIdsChange() {
    this.changeComponent();
  }

  setCategoryInit(categoryId) {
    if(this.subjects.find(s => s.value === this.initFormData.categoryId)) {
      this.subjectSelected = categoryId;
    }
  }

  setDataEdition(data) {
    this.init(
      data.title,
      data.contentSummary,
      data.defaultDueDateForTasks.toString(),
      data.defaultLastDateForTasks.toString(),
      data.suggestedGrade?.id || data.suggestedGradeId,
      data.actualGrade?.id || data.actualGradeId
    );
    this.setCategoryInit(data.categoryId);
    this.switchToggle = data.hasGrading;
    this.selectedSchoolCalendar = this.initSchoolCalendar = data.schoolDay;
    this.automaticFolder = data.automaticFolderType ? data.automaticFolderType : this.automaticFolder;
    this.form.suggestedLessonDuration.value = data.suggestedLessonDuration;

    if (data.img && data.imgHash) {
      this.setImageOnEdition(data.img, data.imgHash.substring(56))
    } else {
      this.loading = false;
    }

    if ((this.productType === this.productTypes.BUILDER || this.productType === this.productTypes.BUILD) && data.components) {
      this.components = data.components;
      if(data.componentsSelected && data.componentsSelected.length > 0) {
        this.componentsSelected = [...data.componentsSelected];
      } else {
        let componentMandatory = this.components.find(c => c.mandatory);
        if (componentMandatory)
          this.componentsSelected = [componentMandatory];
      }
      
      this.componentsSelectedIds = this.componentsSelected.map(c => c.id);
      this.componentsSelectedOriginalIds = cloneDeep(this.componentsSelectedIds);
    }

    if(this.isQuickSubject && typeof this.initFormData.schoolDay === 'object') {
      this.selectedSchoolCalendar = data.schoolDay.schoolDayId;
    } else if (!this.selectedSchoolCalendar) {
      this.selectedSchoolCalendar = this.activeSchoolCalendar;
    }
  }

  async setImageOnEdition(url, imgHash){
    let request = await fetch(url);
    let blob: Blob = await request.blob();    
    let imageName = url.split('.').slice(0, -1).join('.');
    let image: File = new File([blob], imageName);
    this.uploadedImage = {file : image , hash: imgHash }
    this.loading = false;
  }

  changeGrading(){
    if(this.elementId && !this.isQuickSubject && this.productType !== this.productTypes.BUILD) {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$i18n.t('courses.createCourse.confirmGrading'),
          action: async () => {},
          secondAction: async () => {
            this.switchToggle = !this.switchToggle;
          }
        }
      );
    }
  }

  async getSchoolCalendarList() {
    this.schoolCalendarList = await this.$CourseTemplates.Services.getSchoolCalendarOpt(this.organization[0].organizationId);
    let format = 'MMMM, YYYY';
    this.schoolCalendarList = this.schoolCalendarList.map(calendar => {
      return { 
        text: Moment(calendar.startDate).format(format) + ' - ' +  Moment(calendar.endDate).format(format), 
        value: calendar.schoolDayId,
        duration: calendar.duration,
        startDate: calendar.startDate
      } 
    });
  }

  created() {
    if(this.elementId) this.loading = true;
    else this.init();
    if(this.productType === this.productTypes.BUILDER || this.productType === this.productTypes.BUILD) { 
      this.$edEventBus.$on(COURSES_EVENTS.SUBJECT_DESCRIPTION_BUILDER, this.save);
    }
  }

  beforeDestroy() {
    if(this.productType === this.productTypes.BUILDER || this.productType === this.productTypes.BUILD) {
      this.$edEventBus.$off(COURSES_EVENTS.SUBJECT_DESCRIPTION_BUILDER, this.save);
    }
  }

  /* Watch */
  @Watch('organization', {immediate: true})
  async getData() {
    if(this.organization.length > 0) {
      this.organization && CourseService.getCourseCategories(this.organization[0].organizationId)
        .then((res) => {
          this.subjects = res.map(el => {
            return {
              text: el.name,
              value: el.id
            }
          });
          if(this.initFormData?.title) {
            this.setCategoryInit(this.initFormData.categoryId);
          }
        });
      if(this.productType !== this.productTypes.SUBJECT) {
        this.$CourseTemplates.Store.getGradeLevels(this.organization[0].organizationId);
      }
      if(this.productType === this.productTypes.BUILDER && !this.isQuickSubject) { 
        this.getSchoolCalendarList();
      }
    }
  }

  @Watch('initFormData', {immediate: true, deep: true})
  setCalendarListForClass() {
    if (this.isQuickSubject && typeof this.initFormData.schoolDay === 'object') {
      let format = 'MMMM, YYYY';
      this.schoolCalendarList = [{ 
        text: Moment(this.initFormData.schoolDay.startDate).format(format) + ' - ' +  Moment(this.initFormData.schoolDay.endDate).format(format), 
        value: this.initFormData.schoolDay.schoolDayId,
        duration: this.initFormData.schoolDay.duration,
        startDate: this.initFormData.schoolDay.startDate
      }];
    }
  }

  @Watch('initFormData', {immediate: true})
  setInitForm() {
    if(this.initFormData?.title) {
      setTimeout(() => {
        this.setDataEdition(this.initFormData);
      },10)
    }
  }

  @Watch('formDisabled', {immediate: true}) 
  emitDisabedFormEvent() {
    if(this.productType === this.productTypes.BUILDER || this.productType === this.productTypes.BUILD) { 
      this.$emit('disableSubectForm', this.formDisabled);
    }
  }

}

